import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { DocRenderer, DocRendererProps } from '@cyntler/react-doc-viewer';
import { Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Spinner } from 'src/components/Spinner/Spinner';
import { PDFJS_SECURITY_OPTIONS } from 'src/pdfSettings';
import styled from 'styled-components';

export const Component: React.FC<DocRendererProps> = observer(
    ({ mainState: { currentDocument } }) => {
        const [totalPages, setCountPages] = useState(0);
        const [zoom, setZoom] = useState(1);

        const zoomIn = () => {
            setZoom(Math.min(zoom + 0.1, 1.5));
        };

        const zoomOut = () => {
            setZoom(Math.max(zoom - 0.1, 0.2));
        };

        const resetZoom = () => {
            setZoom(1);
        };

        if (!currentDocument) {
            return null;
        }

        return (
            <StyledDiv>
                <StyledPDFDocument
                    file={currentDocument.uri}
                    options={PDFJS_SECURITY_OPTIONS}
                    onLoadSuccess={(pdf) => {
                        setCountPages(pdf.numPages);
                    }}
                    loading={<StyledSpinner />}
                >
                    {Array.from({ length: totalPages }).map((_, index) => {
                        return (
                            <Page
                                scale={zoom}
                                pageNumber={index + 1}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                renderAnnotationLayer={false}
                            />
                        );
                    })}
                </StyledPDFDocument>
                {totalPages ? (
                    <Footer>
                        <StyledButton
                            onClick={() => {
                                zoomOut();
                            }}
                        >
                            <ZoomOutOutlined />
                        </StyledButton>
                        <StyledButton
                            onClick={() => {
                                resetZoom();
                            }}
                        >
                            <StyledSpan>100%</StyledSpan>
                        </StyledButton>
                        <StyledButton
                            onClick={() => {
                                zoomIn();
                            }}
                        >
                            <ZoomInOutlined />
                        </StyledButton>
                    </Footer>
                ) : null}
            </StyledDiv>
        );
    },
) as DocRenderer;

(Component as DocRenderer).fileTypes = ['pdf', 'application/pdf'];
(Component as DocRenderer).weight = 1;

export const PDFRenderer = Component as DocRenderer;

const StyledPDFDocument = styled(Document)`
    position: relative;
    width: fit-content;
    margin: 0 auto;

    canvas {
        transform: translateY(9px);
    }
`;

const StyledDiv = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    overflow: auto;
    margin: 0 auto;
    padding: 50px 0 10px;
    min-height: calc(100vh - 50px);
`;

const Footer = styled.div`
    position: sticky;
    bottom: 0;
    left: calc(50% - 50px);
    margin: 0 auto;
    width: fit-content;
    background: #1e1e1ed9;
`;

const StyledButton = styled(Button)`
    border-radius: 0;
    background: none;
    border: none;
    color: white;
    font-size: 22px;
    height: fit-content;
    line-height: 1;

    &:hover,
    &:active,
    &:focus {
        background: #31313199;
        color: white;
    }

    .anticon {
        vertical-align: 0;
    }
`;

const StyledSpan = styled.span`
    vertical-align: 2px;
    font-size: 14px;
`;

const StyledSpinner = styled(Spinner)`
    color: white !important;
`;

export const extractFirstPageAsImage = async (url: string) => {
    const canvas = document.createElement('canvas');

    try {
        const pdfDoc = pdfjs.getDocument({
            url,
            ...PDFJS_SECURITY_OPTIONS,
        });

        const pdf = await pdfDoc.promise;
        const firstPage = await pdf.getPage(1);
        const viewport = firstPage.getViewport({ scale: 0.5 });

        canvas.style.opacity = '0';
        canvas.style.position = 'fixed';
        canvas.style.zIndex = '-100';
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        document.body.appendChild(canvas);

        const renderTask = firstPage.render({
            canvasContext: canvas.getContext('2d') as any,
            viewport,
        });

        await renderTask.promise;

        return canvas.toDataURL('image/jpeg');
    } catch (error) {
        // Swallowing this silently is what made the empty tiles impossible to
        // diagnose; a failed thumbnail is not fatal, but it must be visible.
        console.error(`Failed to render a PDF thumbnail for ${url}`, error);

        return undefined;
    } finally {
        canvas.remove();
    }
};
